<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const isEditing = computed(() => !!props.campaign);

// Maps form data to WhatsappCampaign model format
const mapToWhatsappCampaign = formDetails => {
  const data = {
    title: formDetails.title,
    inbox_id: formDetails.inbox_id,
    message: formDetails.message,
    template_params: formDetails.template_params,
    send_interval: (Number(formDetails.send_interval_minutes) || 0) * 60 || 5,
    start_time: formDetails.sending_start_time || '08:00',
    end_time: formDetails.sending_end_time || '18:00',
    enabled: true,
  };

  // Map scheduled_at to start_date
  if (formDetails.scheduled_at) {
    const date = new Date(formDetails.scheduled_at);
    data.start_date = date.toISOString().split('T')[0];
  }

  // Map audience contact lists to contact_list_id (first one)
  const contactLists = (formDetails.audience || []).filter(
    a => a.type === 'ContactList'
  );
  if (contactLists.length > 0) {
    data.contact_list_id = contactLists[0].id;
  }

  // Map sender if available
  if (formDetails.sender_id) {
    data.sender_id = formDetails.sender_id;
  }

  return data;
};

const addCampaign = async campaignDetails => {
  try {
    const data = mapToWhatsappCampaign(campaignDetails);
    await store.dispatch('whatsappCampaigns/create', data);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: 'whatsapp',
    });

    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errors?.[0] ||
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const updateCampaign = async campaignDetails => {
  try {
    const data = mapToWhatsappCampaign(campaignDetails);
    await store.dispatch('whatsappCampaigns/update', {
      id: props.campaign.id,
      ...data,
    });

    useAlert('Campanha atualizada com sucesso!');
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errors?.[0] ||
      error?.response?.message ||
      'Erro ao atualizar campanha';
    useAlert(errorMessage);
  }
};

const handleSubmit = campaignDetails => {
  if (isEditing.value) {
    updateCampaign(campaignDetails);
  } else {
    addCampaign(campaignDetails);
  }
};

const handleClose = () => emit('close');
</script>

<template>
  <div
    class="w-[25rem] z-50 min-w-0 absolute top-10 ltr:right-0 rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-md max-h-[80vh] overflow-y-auto"
  >
    <div class="p-6 flex flex-col gap-6">
      <h3 class="text-base font-medium text-n-slate-12 flex-shrink-0">
        {{
          isEditing ? 'Editar Campanha' : t(`CAMPAIGN.WHATSAPP.CREATE.TITLE`)
        }}
      </h3>
      <WhatsAppCampaignForm
        :campaign="campaign"
        @submit="handleSubmit"
        @cancel="handleClose"
      />
    </div>
  </div>
</template>
